import ApiClient from '@afosto/api/ApiClient';

class Subtenants {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Get the default tenant
   *
   * @param {Object} options - The additional request options
   * @description Returns the default tenant
   * @returns {Promise}
   */
  getDefaultSubtenant(options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/default`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of approved authorizations to the tenant&#39;s account
   *
   * @param {Object} options - The additional request options
   * @description Returns a list of subtenants
   * @returns {Promise}
   */
  listAuthorizations(options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/authorizations`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of pending invites for users to the tenant&#39;s account
   *
   * @param {Object} options - The additional request options
   * @description Returns a list of subtenants
   * @returns {Promise}
   */
  listPendingAuthorizationInvites(options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/authorizations/invites`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of pending requests for users to the tenant&#39;s account
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.state]
   * @param {Object} options - The additional request options
   * @description Returns a list of subtenants
   * @returns {Promise}
   */
  listPendingAuthorizationRequests(query = {}, options = {}) {
    const params = {
      state: query.state,
    };

    return this.apiClient.request({
      url: `/iam/subtenants/authorizations/requests`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get a list of pending invites to subtenants
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.state]
   * @param {Object} options - The additional request options
   * @description Returns a list of subtenants
   * @returns {Promise}
   */
  listPendingInvites(query = {}, options = {}) {
    const params = {
      state: query.state,
    };

    return this.apiClient.request({
      url: `/iam/subtenants/invites`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get a list of pending subtenant requests
   *
   * @param {Object} options - The additional request options
   * @description Returns a list of subtenants
   * @returns {Promise}
   */
  listPendingSubtenants(options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/requests`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of accessible subtenants
   *
   * @param {Object} options - The additional request options
   * @description Returns a list of subtenants
   * @returns {Promise}
   */
  listSubtenants(options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Send a request to access another tenant
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a new subtenant request
   * @returns {Promise}
   */
  sendInvite(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/authorizations/invites`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Send a request to access another tenant
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a new subtenant request
   * @returns {Promise}
   */
  sendSubtenantRequest(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/requests`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Update the state of an invite
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates the invite state
   * @returns {Promise}
   */
  updateInvite(id, body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/invites/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update the state of a request
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates the request state
   * @returns {Promise}
   */
  updateRequest(id, body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subtenants/authorizations/requests/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const subtenants = new Subtenants();

export default Subtenants;
